import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { theme as antdTheme } from 'antd';

import { apexThemeMode } from '@totem/components/services/optimize/v2/dashboard/chartUtils';
import {
  BaselineBillMonth,
  MonthlyPerformance,
} from '@totem/components/services/optimize/v2/dashboard/types';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

type Metric = 'kwh' | 'peakKw';

type Props = {
  baselineMonths: BaselineBillMonth[];
  performanceMonths: MonthlyPerformance[];
  metric: Metric;
  height?: number;
};

const monthLabel = (year: number, month: number) =>
  `${MONTH_NAMES[month - 1]} ${year}`;

const MonthlyComparisonChart = ({
  baselineMonths,
  performanceMonths,
  metric,
  height = 300,
}: Props) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  // Split performance months into actual vs projected
  const actualMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'actual',
  );
  const projectedMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'projected',
  );
  const allPerfMonths = [...actualMonths, ...projectedMonths];

  const getValue = (
    mon: MonthlyPerformance,
    field: 'actual' | 'baseline',
  ): number => {
    if (field === 'actual') {
      return parseFloat(
        (metric === 'kwh' ? mon.actualKwh : mon.actualPeakKw).toFixed(1),
      );
    }
    return parseFloat(
      (metric === 'kwh' ? mon.baselineKwh : mon.baselinePeakKw).toFixed(1),
    );
  };

  const baselineValues =
    metric === 'kwh'
      ? baselineMonths.map((mon) => parseFloat(mon.kwh.toFixed(1)))
      : baselineMonths.map((mon) => parseFloat(mon.peakKw.toFixed(1)));

  const allCategories = [
    ...baselineMonths.map((mon) => monthLabel(mon.year, mon.month)),
    ...allPerfMonths.map((mon) => monthLabel(mon.year, mon.month)),
  ];

  const nullBase = new Array(baselineMonths.length).fill(null);
  const nullPerf = new Array(allPerfMonths.length).fill(null);

  // Baseline series: real values for baseline period, then nulls
  const baselineSeries = [...baselineValues, ...nullPerf];

  // Expected (average baseline) series: nulls for baseline period, then per-month values
  const expectedSeries = [
    ...nullBase,
    ...allPerfMonths.map((mon) => getValue(mon, 'baseline')),
  ];

  // Actual series: nulls for baseline period + projected months; real values for actual months
  const actualSeries = [
    ...nullBase,
    ...allPerfMonths.map((mon) =>
      mon.entryType === 'actual' ? getValue(mon, 'actual') : null,
    ),
  ];

  // Projected series: nulls for baseline period + actual months; projected values only
  const projectedSeries = [
    ...nullBase,
    ...allPerfMonths.map((mon) =>
      mon.entryType === 'projected' ? getValue(mon, 'actual') : null,
    ),
  ];

  const metricLabel = metric === 'kwh' ? 'kWh' : 'Peak kW';

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      stacked: false,
      toolbar: { show: false },
      background: 'transparent',
    },
    theme: { mode: apexThemeMode(token.colorBgBase) },
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: '60%',
      },
    },
    fill: {
      // baseline, expected, actual solid, projected faded
      opacity: [1, 1, 1, 0.4],
    },
    xaxis: {
      categories: allCategories,
      labels: {
        rotate: -45,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: metricLabel },
      labels: {
        formatter: (val: number) =>
          val !== null && val !== undefined
            ? val.toLocaleString('en-US', { maximumFractionDigits: 0 })
            : '',
      },
    },
    colors: ['#5B8FF9', '#94D2BD', '#F4A261', '#F4A261'],
    legend: { show: true, position: 'top' },
    tooltip: {
      y: {
        formatter: (val: number) =>
          val !== null && val !== undefined
            ? `${val.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${metricLabel}`
            : 'N/A',
      },
    },
    dataLabels: { enabled: false },
  };

  const series = [
    { name: `Baseline ${metricLabel}`, data: baselineSeries },
    { name: `Expected ${metricLabel}`, data: expectedSeries },
    { name: `Actual ${metricLabel}`, data: actualSeries },
    { name: `Projected ${metricLabel}`, data: projectedSeries },
  ];

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="bar"
      height={height}
    />
  );
};

export default MonthlyComparisonChart;
